/**
 * Splash de bienvenida PWA · Jun 2026
 *
 * Comportamiento:
 *  - Se muestra UNA sola vez por dispositivo (mientras no se borre localStorage).
 *  - El usuario elige rol (Cliente / Profesional). Su elección queda persistida en
 *    `localStorage.ag_role_preference` y nunca más vuelve a ver el splash.
 *  - Si vuelve a entrar en la PWA en una sesión futura → se le redirige automáticamente
 *    a `/marketplace` (cliente) o `/login` con preset (profesional).
 *
 * Diseño basado en mockup proporcionado por el usuario, adaptado al stack actual
 * (Phosphor icons, Tailwind, React Router, theme provider). Sin tocar ningún
 * componente existente — sólo se inserta el splash al inicio del flujo.
 */
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../lib/theme";
import { AutoGlowBrand } from "./components/AutoGlowLogo";
import {
  UserCircle, Briefcase, CaretRight, Sun, Moon,
} from "@phosphor-icons/react";

const ROLE_KEY = "ag_role_preference"; // 'client' | 'pro'
const SPLASH_SEEN_KEY = "ag_splash_seen_v1";

/** Helpers públicos para que otras páginas puedan inspeccionar la elección. */
export function getRolePreference() {
  try { return localStorage.getItem(ROLE_KEY); } catch { return null; }
}
export function setRolePreference(role) {
  try {
    localStorage.setItem(ROLE_KEY, role);
    localStorage.setItem(SPLASH_SEEN_KEY, "1");
  } catch { /* ignore (modo privado) */ }
}
export function shouldShowSplash() {
  try { return !localStorage.getItem(SPLASH_SEEN_KEY); } catch { return false; }
}

export default function WelcomeSplash() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();

  // Solo auto-redirige si se llega aquí SIN intención explícita (vía PublicRootGate
  // en "/" tras haber elegido rol). Si el usuario pulsa el logo y aterriza en
  // "/welcome" directamente, mostramos SIEMPRE el splash — esto es la puerta
  // de entrada del ecosistema y nunca debe quedar bloqueada.
  useEffect(() => {
    const isExplicitWelcome = location.pathname === "/welcome";
    if (isExplicitWelcome) return; // jamás redirigir si el usuario pidió /welcome
    const role = getRolePreference();
    if (role === "client") navigate("/marketplace", { replace: true });
    else if (role === "pro") navigate("/login", { replace: true });
  }, [navigate, location.pathname]);

  // Si ya eligió antes Y el componente está montado desde PublicRootGate (path "/"),
  // devolvemos null mientras se redirige. En cambio, en "/welcome" siempre renderizamos.
  if (location.pathname !== "/welcome" && getRolePreference()) return null;

  const pickRole = (role) => {
    setRolePreference(role);
    navigate(role === "client" ? "/marketplace" : "/login", { replace: true });
  };

  const isDark = theme !== "light";

  return (
    <main
      data-testid="welcome-splash"
      className="w-full min-h-[100dvh] flex flex-col items-center justify-between py-10 px-5 overflow-hidden relative"
      style={{ backgroundColor: isDark ? "#0F1117" : "#FAFAF7" }}
    >
      {/* Grain texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, ${isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.3)"} 1px, transparent 1px)`,
          backgroundSize: "28px 28px",
          opacity: isDark ? 0.035 : 0.04,
        }}
      />

      {/* Toggle tema */}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label="Cambiar tema"
        data-testid="welcome-theme-toggle"
        className="anim-theme-btn absolute top-6 right-5 z-20 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 active:scale-90"
        style={{
          backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"}`,
        }}
      >
        {isDark
          ? <Sun size={18} weight="fill" style={{ color: "var(--ag-gold)" }} />
          : <Moon size={18} weight="fill" style={{ color: "var(--ag-midnight)" }} />}
      </button>

      {/* Logo central · Sprint P.1.5 · Bloque L (CP4): tamaño reducido en mobile
          y microcopy value-prop multi-sector debajo, para elevar los CTAs above-the-fold
          y aumentar densidad útil (Fix P0-15 audit CP2). */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-10 w-full max-w-md mx-auto py-6">
        <div className="anim-logo-wrapper flex flex-col items-center select-none">
          <AutoGlowBrand size={48} />
          <p
            className="anim-subtitle text-sm mt-5 text-center font-medium tracking-wide max-w-xs"
            style={{
              color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
              letterSpacing: "0.04em",
            }}
          >
            El ecosistema profesional para el cuidado del motor.
          </p>
          <p
            className="mt-2 text-[11px] text-center tracking-wide max-w-xs"
            style={{ color: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.40)" }}
            data-testid="welcome-value-prop"
          >
            Talleres, detailers, chapistas, neumáticos y más — todo en un mismo sitio.
          </p>
        </div>
      </div>

      {/* Botones de elección de rol */}
      <div className="w-full max-w-md mx-auto flex flex-col gap-3.5 relative z-10 pb-2">
        {/* Cliente */}
        <button
          type="button"
          onClick={() => pickRole("client")}
          data-testid="welcome-pick-client"
          className="anim-btn-client w-full group relative overflow-hidden rounded-2xl transition-all duration-300 active:scale-[0.98]"
          style={{ minHeight: 72 }}
        >
          <div
            className="absolute inset-0 rounded-2xl transition-all duration-300"
            style={{
              backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              border: `1.5px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"}`,
            }}
          />
          <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
               style={{ backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }} />
          <div className="relative flex items-center px-5 py-4 gap-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
              style={{ backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}
            >
              <UserCircle size={20} weight="duotone" style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }} />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-base font-semibold whitespace-nowrap"
                 style={{ color: isDark ? "#E5E5E5" : "#1A1A1A" }}>
                Soy cliente
              </p>
              <p className="text-xs mt-0.5 truncate"
                 style={{ color: isDark ? "rgba(255,255,255,0.40)" : "rgba(0,0,0,0.55)" }}>
                Busco un profesional cerca de mí
              </p>
            </div>
            <CaretRight
              size={18} weight="bold"
              className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
              style={{ color: isDark ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.30)" }}
            />
          </div>
        </button>

        {/* Profesional */}
        <button
          type="button"
          onClick={() => pickRole("pro")}
          data-testid="welcome-pick-pro"
          className="anim-btn-pro w-full group relative overflow-hidden rounded-2xl transition-all duration-300 active:scale-[0.98]"
          style={{ minHeight: 72 }}
        >
          <div
            className="absolute inset-0 rounded-2xl transition-all duration-300"
            style={{
              background: "linear-gradient(135deg, #D4AF37 0%, #C59B2E 50%, #B8860B 100%)",
            }}
          />
          <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
               style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%)" }} />
          <div className="relative flex items-center px-5 py-4 gap-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                 style={{ backgroundColor: "rgba(255,255,255,0.18)" }}>
              <Briefcase size={20} weight="duotone" style={{ color: "#FFFFFF" }} />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-base font-semibold whitespace-nowrap" style={{ color: "#FFFFFF" }}>
                Soy profesional
              </p>
              <p className="text-xs mt-0.5 truncate" style={{ color: "rgba(255,255,255,0.75)" }}>
                Quiero digitalizar mi negocio · 30 días gratis
              </p>
            </div>
            <CaretRight size={18} weight="bold"
                        className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
                        style={{ color: "rgba(255,255,255,0.75)" }} />
          </div>
        </button>

        <p className="anim-version text-[11px] text-center mt-2"
           style={{ color: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.30)" }}>
          AutoGlow Ecosystem v1.0
        </p>

        {/* Acceso rápido para volver al diseño antiguo (Landing completa) */}
        <button
          type="button"
          onClick={() => navigate("/home")}
          data-testid="welcome-skip"
          className="text-[10px] uppercase tracking-widest underline-offset-4 hover:underline mt-1"
          style={{ color: isDark ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.40)" }}
        >
          Ver landing completa
        </button>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes splashFadeIn { 0% { opacity: 0; transform: translateY(12px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes splashLogoGlow { 0% { opacity: 0; transform: scale(0.92); filter: blur(4px); } 60% { opacity: 1; transform: scale(1.01); filter: blur(0); } 100% { opacity: 1; transform: scale(1); filter: blur(0); } }
        @keyframes splashSlideUp { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes splashVersionFade { 0% { opacity: 0; } 100% { opacity: 1; } }
        @keyframes splashThemeBtnIn { 0% { opacity: 0; transform: rotate(-180deg) scale(0); } 60% { opacity: 1; transform: rotate(10deg) scale(1.1); } 100% { opacity: 1; transform: rotate(0) scale(1); } }
        .anim-logo-wrapper { animation: splashLogoGlow 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .anim-subtitle { animation: splashFadeIn 0.55s 0.25s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .anim-btn-client { animation: splashSlideUp 0.55s 0.45s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .anim-btn-pro { animation: splashSlideUp 0.55s 0.60s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .anim-version { animation: splashVersionFade 0.5s 0.85s ease-out both; }
        .anim-theme-btn { animation: splashThemeBtnIn 0.5s 0.10s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
      `}</style>
    </main>
  );
}
